<script setup lang="ts">
// Live audio peak meters (dBFS) with a short peak hold. `size` mini for list
// rows, full for detail panels (full also prints the held peak under each
// bar). Colour by level: red above -3, green down to -18, amber down to -50,
// grey below.
import { ref, watch } from 'vue'
import { band } from '../audiohist'

const props = defineProps<{ db: number[]; labels?: string[]; size?: 'mini' | 'full' }>()
const holds = ref<{ v: number; t: number }[]>([])
watch(
    () => props.db,
    (db) => {
        const now = Date.now()
        holds.value = db.map((v, i) => {
            const h = holds.value[i]
            return h && h.v >= v && now - h.t < 1500 ? h : { v, t: now }
        })
    },
    { immediate: true },
)
const level = (db: number) => Math.max(0, Math.min(1, (db + 60) / 60))
// the bands the audio history uses too, its silence at -60 (WEB-48: the
// meters said silent below -50, the history below -60)
const cls = band
const fillBg = { hot: 'bg-bad', good: 'bg-ok', low: 'bg-warn', silent: 'bg-off min-h-px' }
const numFg = { hot: 'text-bad', good: 'text-fg-2', low: 'text-fg-2', silent: 'text-fg-3' }
const num = (db: number) => (db <= -99 ? '−∞' : db.toFixed(0))
</script>

<template>
    <div class="flex items-end gap-0.75">
        <div
            v-for="(v, i) in db"
            :key="i"
            class="flex flex-col items-center gap-0.5"
            :class="size === 'mini' ? 'w-1' : 'max-w-7.5 min-w-4.5 flex-1'"
            :title="`${labels?.[i] ?? 'channel ' + (i + 1)}: ${v <= -99 ? 'silent' : v.toFixed(1) + ' dBFS'}`"
        >
            <div
                class="relative flex w-full items-end overflow-hidden rounded-xs bg-bg-3"
                :class="size === 'mini' ? 'h-4' : 'h-19.5'"
            >
                <div
                    class="w-full transition-[height] duration-100 ease-linear"
                    :class="fillBg[cls(v)]"
                    :style="{ height: level(v) * 100 + '%' }"
                />
                <div
                    v-if="holds[i] && holds[i]!.v > -99"
                    class="absolute inset-x-0 h-0.5 bg-fg opacity-80"
                    :style="{ bottom: level(holds[i]!.v) * 100 + '%' }"
                />
            </div>
            <template v-if="(size ?? 'full') === 'full'">
                <span class="font-mono text-[10px] tabular-nums" :class="numFg[cls(holds[i]?.v ?? v)]">{{
                    num(holds[i]?.v ?? v)
                }}</span>
                <span class="text-[10px] text-fg-3">{{ labels?.[i] ?? i + 1 }}</span>
            </template>
        </div>
    </div>
</template>
